import { serviceTypeBadge } from '@/components/features/offerings/common';
import { LoaderElement } from '@/components/features/shared/LoaderElement';
import { NavHeader } from '@/components/features/shared/NavHeader';
import { UnexpectedError } from '@/components/features/shared/UnexpectedError';
import { PriceLabel, PriceLabelWithTaxResponsive, renderSinglePeriod } from '@/components/features/shared/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CollectorStatus } from '@/domain/collectors/collector';
import { BillingType } from '@/domain/invoices/invoice';
import { ServiceType } from '@/domain/offerings/offering';
import { PaymentMethod, PaymentStatus } from '@/domain/payments/payments';
import { useAgreement } from '@/hooks/api/useAgreements';
import { useCollectors } from '@/hooks/api/useCollectors';
import { useOffering } from '@/hooks/api/useOfferings';
import { useParcelByContract } from '@/hooks/api/useParcels';
import { useAgreementCustomer, useAgreementPayment, useAgreementPayments, useInstallmentPayment } from '@/hooks/api/usePayments';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { CustomerData } from './CustomerData';
import { ParcelContractData } from './ParcelContractData';
import { PaymentAccreditationForm, paymentFormSchema, type PaymentFormValues } from './PaymentAccreditationForm';
import { SubtotalsData } from './SubtotalsData';
import { useOnSubmit } from './useOnSubmit';
import { PageContainer } from '@/components/features/shared/PageContainer';

export const SinglePayment = ({ paymentId, contractId }: { paymentId: string, contractId: string }) => {
  const { data: agreement, isLoading: isLoadingAgreement, error: errorAgreement } = useAgreement(contractId)
  const { data: offering, isLoading: isLoadingOffering, error: errorOffering } = useOffering(agreement?.offeringId ?? '')
  const { data: agreementPayments, isLoading: isLoadingAgreementPayment, error: errorAgreementPayment, refetch: refetchAgreementPayments } = useAgreementPayment(contractId, paymentId);
  const { data: customer, isLoading: isLoadingCustomer } = useAgreementCustomer(contractId);
  const { data: paymentData, isLoading: isLoadingPayments } = useAgreementPayments(contractId)

  // const hasSubContracts = agreementPayments?.some(p => p.contractId !== contractId)
  const { data: installmentPaymentInfo } = useInstallmentPayment(contractId, paymentId);

  // console.log(agreementPayments, installmentPaymentInfo)

  const { data: parcel, isLoading: isLoadingParcel } = useParcelByContract({
    contractId, enabled: offering?.serviceType === ServiceType.PARCELAS
  });

  const { data: collectors = [], isLoading: isLoadingCollectors } = useCollectors({ status: CollectorStatus.Active });

  const isLoading = isLoadingAgreement || isLoadingOffering || isLoadingPayments || isLoadingAgreementPayment || isLoadingParcel || isLoadingCustomer || isLoadingCollectors;
  const error = errorAgreement || errorOffering || errorAgreementPayment;

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      billingType: BillingType.Invoice,
      paymentMethod: PaymentMethod.BANK_TRANSFER,
      paymentDetails: '',
      paymentVerified: false,
      partialPayment: false,
      amount: undefined,
      interestAmount: undefined,
      collectorId: agreement?.collectorId || null,
    },
  });

  const billingType = form.watch('billingType', BillingType.Invoice)
  const selectedPaymentMethod = form.watch('paymentMethod');
  const isPartialPayment = form.watch('partialPayment');
  const amount = form.watch('amount');
  const interestAmount = form.watch('interestAmount');
  const selectedCollectorId = form.watch('collectorId');

  const hasContractCollector = !!agreement?.collectorId
  const selectedDifferentCollector = !!(hasContractCollector && selectedCollectorId && selectedCollectorId !== agreement.collectorId);
  const assignedCollectorName = hasContractCollector && agreement.collectorId ? collectors.find(c => c.id === agreement.collectorId)?.name : null

  const totalAmountToBePayed = agreementPayments?.[0]?.totalAmount || 0
  const amountPayed = paymentData?.filter(d => d.contractId === agreementPayments?.[0].contractId)?.[0]?.payments
    ?.filter(p => p.status !== PaymentStatus.PENDING && p.status !== PaymentStatus.REFUNDED)?.reduce((total, payment) => total + payment.amount, 0) || 0
  const amountOwed = amountPayed > 0 ? (agreement?.amount ? agreement.amount - amountPayed : 0) : 0
  const totalAmount = (amountOwed > 0 ? agreement?.amount : totalAmountToBePayed) || 0

  const cardInterest = selectedPaymentMethod === PaymentMethod.CREDIT_CARD ? (interestAmount || 0) : 0;
  const amountToInvoice = ((isPartialPayment ? amount : (billingType === BillingType.Receipt && amountOwed > 0 ? amountOwed : totalAmount)) || 0) + cardInterest;

  const partialPaymentsEnabled = !installmentPaymentInfo
  const amountToCharge = ((isPartialPayment ? amount : (totalAmountToBePayed || totalAmount)) || 0) + cardInterest;

  const isInitialPaymentForPP = installmentPaymentInfo?.number === 0
    && (installmentPaymentInfo.initialAmount || 0) > 0;

  useEffect(() => {
    if (isPartialPayment) {
      form.setValue('billingType', BillingType.Receipt);
    }
  }, [isPartialPayment]);

  useEffect(() => {
    if (offering) {
      form.setValue('billingType', offering.isInvoiceable ? BillingType.Invoice : BillingType.Receipt)
    }
  }, [offering])

  useEffect(() => {
    const interval = setInterval(() => {
      refetchAgreementPayments();
    }, 60000);
    return () => clearInterval(interval);
  }, [refetchAgreementPayments]);

  const { onSubmit, isPending } = useOnSubmit({ contractId, paymentId, amountToCharge, customer });

  if (isLoading) {
    return <LoaderElement />
  }

  if (error || !agreementPayments) {
    return <UnexpectedError />
  }

  return (
    <PageContainer className='max-w-4xl'>
      <NavHeader>
        <h1 className="text-2xl font-bold tracking-tight">
          Confirmación del pago y emisión de comprobante
        </h1>
        <p className='text-muted-foreground'>Complete los datos para confirmar la recepción del pago y emitir un comprobante</p>
      </NavHeader>

      <div className="grid grid-cols-1 gap-4 mt-6">
        <Card className="h-full border-gray-200 shadow-sm flex flex-col overflow-hidden">
          <div className="bg-gray-900 text-white p-6">
            <h2 className="text-lg font-semibold flex items-center justify-between">
              <span>Resumen de Operación</span>
              <span className="text-xs font-mono bg-white/20 px-2 py-1 rounded">{agreement?.contractNumber}</span>
            </h2>
          </div>

          <CardContent className="p-0 flex-1 flex flex-col bg-white">
            <CustomerData customer={customer} />
            <div className="p-6 border-b border-gray-100 flex-1">
              <div className='flex items-center gap-10 mb-5'>
                <div>
                  <h3 className="text-xs font-bold text-primary mb-3 uppercase tracking-wider">
                    Tipo de servicio a cobrar
                  </h3>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    {offering?.serviceType && <>{serviceTypeBadge(offering.serviceType)}</>}
                  </div>
                </div>
                <div className="flex-1">
                  {parcel && (
                    <ParcelContractData parcel={parcel} />
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-[2fr_6fr_3fr] items-center text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2">
                  <span>Codigo</span>
                  <span>Concepto</span>
                  <span>Monto</span>
                </div>

                {installmentPaymentInfo ? (
                  <div className="grid grid-cols-[2fr_6fr_3fr] items-center pt-1">
                    <div className="w-10">
                      <span className="text-sm font-medium text-gray-900 leading-snug">
                        {offering?.code}
                      </span>
                    </div>
                    <div className="pr-4 flex-1">
                      <div className="text-sm font-medium text-gray-900 leading-snug">
                        {offering?.serviceType === ServiceType.ABONOS ? (
                          <p>
                            {`Anticipo de servicio`}
                            <br />
                            {isInitialPaymentForPP ? (
                              "Entrega Inicial"
                            ) : (
                              `Cuota ${installmentPaymentInfo.number} de ${installmentPaymentInfo.total} - ${renderSinglePeriod(installmentPaymentInfo.periodDate, '/')}`
                            )}
                          </p>
                        ) : (
                          <p>
                            {isInitialPaymentForPP ? (
                              "Entrega Inicial"
                            ) : (
                              `Cuota ${installmentPaymentInfo.number} de ${installmentPaymentInfo.total} - ${renderSinglePeriod(installmentPaymentInfo.periodDate, '/')}`
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                      <PriceLabel
                        price={isInitialPaymentForPP ? (installmentPaymentInfo.initialAmount || 0) : (installmentPaymentInfo.value || 0)}
                      />
                    </div>
                  </div>
                ) : (
                  agreementPayments.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-[2fr_6fr_3fr] items-center">
                      <div className="w-fit">
                        <span className="text-sm font-medium text-gray-900 leading-snug">
                          {item.offeringCode}
                        </span>
                      </div>
                      <div className="pr-4 flex-1">
                        <div className="text-sm font-medium text-gray-900 leading-snug">
                          {item.offeringName}
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        <PriceLabelWithTaxResponsive price={item.totalAmount || 0} taxRate={item.taxRate} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <SubtotalsData amountOwed={amountOwed} amountPayed={amountPayed} interestAmount={interestAmount} amountToCharge={amountToCharge} />
          </CardContent>
        </Card>

        <Card className="h-full border-gray-200 shadow-sm">
          <CardHeader className="bg-white pb-4 border-b">
            <CardTitle className="text-xl">Acreditación del pago</CardTitle>
            <CardDescription>Complete la información del pago a recibir por parte del cliente</CardDescription>
          </CardHeader>
          <CardContent className="pt-6 bg-white">
            <PaymentAccreditationForm
              form={form}
              onSubmit={onSubmit}
              isPending={isPending}
              partialPaymentsEnabled={partialPaymentsEnabled}
              maxPartialAmount={amountOwed > 0 ? amountOwed : totalAmount}
              amountToInvoice={amountToInvoice}
              amountToCharge={amountToCharge}
              activeCollectors={collectors}
              assignedCollectorName={assignedCollectorName}
              selectedDifferentCollector={selectedDifferentCollector}
              amountOwed={amountOwed}
              contractId={contractId}
            />
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
